import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';

const UploadNotificationPopup = ({ 
  notifications = [], 
  onClose, 
  onClearAll
}) => {
  const [expanded, setExpanded] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 파일명에서 확장자 추출하여 아이콘 가져오기
  const getFileIconFromName = (fileName) => {
    if (!fileName) return <InsertDriveFileIcon sx={{ color: '#5f6368' }} />;
    
    const lastDotIndex = fileName.lastIndexOf('.');
    const extension = lastDotIndex !== -1 ? fileName.substring(lastDotIndex + 1).toLowerCase() : '';
    
    // 간단한 파일 아이콘 처리
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) {
      return <InsertDriveFileIcon sx={{ color: '#34a853' }} />;
    }
    if (extension === 'pdf') {
      return <InsertDriveFileIcon sx={{ color: '#ea4335' }} />;
    }
    if (['doc', 'docx'].includes(extension)) {
      return <InsertDriveFileIcon sx={{ color: '#4285f4' }} />;
    }
    if (['xls', 'xlsx', 'csv'].includes(extension)) {
      return <InsertDriveFileIcon sx={{ color: '#0f9d58' }} />;
    }
    
    // 기본 파일 아이콘
    return <InsertDriveFileIcon sx={{ color: '#5f6368' }} />;
  };

  // 상태에 따른 아이콘 반환
  const getStatusIcon = (status, type) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon sx={{ color: '#34a853', fontSize: 20 }} />;
      case 'error':
        return <ErrorIcon sx={{ color: '#ea4335', fontSize: 20 }} />;
      case 'deleting':
        return <DeleteIcon sx={{ color: '#ff9800', fontSize: 20 }} />;
      default:
        return null;
    }
  };

  // 완료된 항목 수 계산
  const completedCount = notifications.filter(n => n.status === 'success' || n.status === 'error').length;
  const successCount = notifications.filter(n => n.status === 'success').length;
  const errorCount = notifications.filter(n => n.status === 'error').length;

  // 진행 중인 작업 확인
  const hasUploadingItems = notifications.some(n => n.status === 'uploading');
  const hasDeletingItems = notifications.some(n => n.status === 'deleting');
  const hasProgressItems = hasUploadingItems || hasDeletingItems;

  // 작업 유형별 카운트
  const uploadItems = notifications.filter(n => n.type !== 'delete');
  const deleteItems = notifications.filter(n => n.type === 'delete');

  // 제목 텍스트 생성
  const getTitleText = () => {
    if (hasProgressItems) {
      if (hasDeletingItems && hasUploadingItems) {
        return `항목 ${notifications.length}개 처리 중...`;
      } else if (hasDeletingItems) {
        return `항목 ${deleteItems.length}개 삭제 중...`;
      } else {
        return `항목 ${uploadItems.length}개 업로드 중...`;
      }
    }
    
    if (errorCount > 0) {
      return `항목 ${completedCount}개 중 ${errorCount}개 실패`;
    }
    
    if (deleteItems.length > 0 && uploadItems.length > 0) {
      return `항목 ${completedCount}개 처리 완료`;
    } else if (deleteItems.length > 0) {
      return `항목 ${completedCount}개 삭제 완료`;
    } else {
      return `항목 ${completedCount}개 업로드 완료`;
    }
  };

  // 파일명 줄임 처리
  const truncateFileName = (fileName, maxLength = 25) => {
    if (!fileName || fileName.length <= maxLength) return fileName;
    const extension = fileName.substring(fileName.lastIndexOf('.'));
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 3);
    return `${truncatedName}...${extension}`;
  };

  if (notifications.length === 0) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: isMobile ? 16 : 24,
        right: isMobile ? 16 : 24,
        width: isMobile ? 'calc(100vw - 32px)' : 360,
        maxWidth: isMobile ? '100%' : 400,
        maxHeight: isMobile ? 'calc(100vh - 100px)' : 500,
        backgroundColor: '#fff',
        borderRadius: 2,
        overflow: 'hidden',
        zIndex: 1300,
        border: '1px solid #e0e0e0',
      }}
    >
      {/* 헤더 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 500,
            color: hasUploadingItems ? '#1976d2' : errorCount > 0 ? '#ea4335' : '#34a853',
            flex: 1,
          }}
        >
          {getTitleText()}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ mr: 0.5 }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <IconButton
            size="small"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* 콘텐츠 */}
      <Collapse in={expanded}>
        <Box sx={{ maxHeight: isMobile ? 300 : 400, overflow: 'auto' }}>
          <List dense sx={{ p: 0 }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getFileIconFromName(notification.fileName)}
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 400,
                          color: '#202124',
                        }}
                      >
                        {truncateFileName(notification.fileName)}
                      </Typography>
                    }
                    secondary={
                      notification.status === 'uploading' ? (
                        <Typography variant="caption" sx={{ color: '#1976d2' }}>
                          업로드 중...
                        </Typography>
                      ) : notification.status === 'success' ? (
                        <Typography variant="caption" sx={{ color: '#34a853' }}>
                          완료
                        </Typography>
                      ) : (
                        <Typography variant="caption" sx={{ color: '#ea4335' }}>
                          {notification.error || '서버 오류 발생'}
                        </Typography>
                      )
                    }
                  />
                  
                  <Box sx={{ ml: 1 }}>
                    {getStatusIcon(notification.status, notification.type)}
                  </Box>
                </ListItem>
                
                {index < notifications.length - 1 && (
                  <Divider sx={{ mx: 2 }} />
                )}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Collapse>

      {/* 푸터 (완료된 항목이 있고 에러가 있는 경우만 표시) */}
      {!hasUploadingItems && errorCount > 0 && (
        <Box
          sx={{
            p: 1.5,
            backgroundColor: '#fef7e0',
            borderTop: '1px solid #e0e0e0',
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" sx={{ color: '#ea4335' }}>
            일부 파일 업로드에 실패했습니다
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default UploadNotificationPopup;